import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import reportsService from '../service/reportsService.mjs';
import { useFiscalReport } from '../hook/useFiscalReport.mjs';
import { shortDate } from '../util/format.mjs';
import { periodText as periodTextOf, periodWindow, namedRegion } from '../util/period.mjs';
import {
  downloadAuditOutcomesExcel, outcomeLabel, claimTypeLabel, rateLabel,
} from '../util/auditOutcomesExcel.mjs';
import {
  ScopeToolbar, StatTiles, LoadingCard, ErrorCard, Section, SearchBox, EmptyRow, TH, THR, TD, TDR,
} from './ReportParts.mjs';

const ROLE_LABEL = {
  regional_director: 'Regional Director', global_director: 'Global Director',
  finance_director: 'Finance Director', admin: 'Admin', auditor: 'Auditor',
};

/** Decisions per page; 10 to start, up to 50 at a time. */
const PAGE_SIZES = [10, 25, 50];

const OUTCOMES = [
  { key: 'all', label: 'All' },
  { key: 'approved', label: 'Approved' },
  { key: 'returned', label: 'Returned' },
];

const dateTime = (v) => (v ? new Date(v).toLocaleString('en-US', {
  year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
}) : '-');

const shortId = (id) => (id ? String(id).slice(-8).toUpperCase() : '');

/**
 * The auditor's decisions for the period: what was approved, what was sent
 * back and why, reports rejected, and claims the directors flagged.
 *
 * These are outcomes - decisions already made - not work waiting, so they
 * belong in Reports. The on-screen view is the auditor dashboard's
 * "Rejection report" workbook, and Download Excel builds that same workbook
 * through the shared util.
 */
const RejectionsTab = ({ scope }) => {
  const navigate = useNavigate();
  const { fy, quarter, region, addRegions } = scope;
  const load = useCallback(() => reportsService.getAudit({ fy, quarter, region }), [fy, quarter, region]);
  const { data, loading, error, status, fiscalYears, reload } = useFiscalReport(load, fy);
  const [outcome, setOutcome] = useState('all');
  const [query, setQuery] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  // Decisions are paged: a fiscal year runs to hundreds of rows. Up to 50 a
  // page; the Excel download always holds every decision in the period.
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [page, setPage] = useState(0);

  useEffect(() => {
    addRegions([...(data?.regions || []), ...(data?.byRegion || []).map(r => r.region)]);
  }, [data, addRegions]);

  const decisions = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.decisionsList || []).filter(d =>
      (outcome === 'all' || (outcome === 'returned') === (d.outcome === 'returned'))
      && (!q || [d.userName, d.region, d.auditor, d.comments, d.via, shortId(d.claimId), shortId(d.reportId)]
        .some(v => String(v || '').toLowerCase().includes(q))));
  }, [data, outcome, query]);

  // Back to the first page whenever what is listed changes - a new period,
  // region, outcome, search or page size.
  useEffect(() => { setPage(0); }, [data, outcome, query, pageSize]);

  const pageCount = Math.max(1, Math.ceil(decisions.length / pageSize));
  const currentPage = Math.min(page, pageCount - 1);
  const pageStart = currentPage * pageSize;
  const pageRows = decisions.slice(pageStart, pageStart + pageSize);

  if (status === 403) {
    return <ErrorCard error="Audit decisions are shown to the auditor, and to anyone covering for them, only." />;
  }
  if (!data && loading) return <LoadingCard text="Loading audit decisions..." />;
  if (error && !data) return <ErrorCard error={`Could not load audit decisions: ${error}`} onRetry={reload} />;

  const periodText = periodTextOf(fy, quarter);
  const totals = data?.totals || { decisions: 0, approved: 0, returned: 0, returnRate: null };
  const byRegion = data?.byRegion || [];
  const rejections = data?.reportRejections?.list || [];
  const flags = data?.directorFlags?.list || [];
  const openReport = (id) => navigate(`/monthly-report/${id}`, { state: { from: scope.here } });

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const { from, to } = periodWindow(fy, quarter);
      await downloadAuditOutcomesExcel(data, { region: namedRegion(scope), periodLabel: periodText, from, to });
    } catch (err) {
      setExportError(`Could not generate the rejection report: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  const hasAnything = totals.decisions > 0 || rejections.length > 0 || flags.length > 0;

  return (
    <>
      <ScopeToolbar
        scope={scope}
        fiscalYears={fiscalYears}
        loading={loading}
        note="Decisions made in this period. Report rejections are counted once per report and kept out of the claim return rate; director flags are listed separately too."
      />
      {(error || exportError) && (
        <div className="mb-4"><ErrorCard error={exportError || `Could not refresh: ${error}`} /></div>
      )}

      <StatTiles tiles={[
        { label: 'Decisions', value: String(totals.decisions), hint: periodText },
        { label: 'Approved', value: String(totals.approved) },
        { label: 'Returned', value: String(totals.returned), hint: 'Includes claims flagged during report review' },
        { label: 'Return rate', value: rateLabel(totals.returnRate) },
        {
          label: 'Reports rejected',
          value: String(data?.reportRejections?.count ?? rejections.length),
          hint: `${data?.reportRejections?.claimsAffected ?? 0} claims caught up in them`,
        },
      ]} />

      <div className="flex justify-end mb-4 sm:mb-6">
        <button
          onClick={handleExport}
          disabled={exporting || !data || !hasAnything}
          className="min-h-[44px] sm:min-h-0 px-4 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors disabled:opacity-50"
        >
          {exporting ? 'Building...' : 'Download Excel'}
        </button>
      </div>

      <Section title="By region" subtitle={periodText}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stanford-red text-white">
              <tr>
                <th className={TH}>Region</th>
                <th className={THR}>Approved</th>
                <th className={THR}>Returned</th>
                <th className={THR}>Return rate</th>
              </tr>
            </thead>
            <tbody>
              {byRegion.map(r => (
                <tr key={r.region} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className={`${TD} font-medium text-gray-800`}>{r.region}</td>
                  <td className={TDR}>{r.approved}</td>
                  <td className={TDR}>{r.returned}</td>
                  <td className={`${TDR} font-medium`}>{rateLabel(r.returnRate)}</td>
                </tr>
              ))}
              {byRegion.length === 0 && <EmptyRow colSpan={4} text={`No audit decisions in ${periodText}.`} />}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Decisions" subtitle="Every claim decision the auditor recorded in the period">
        <div className="px-4 sm:px-6 py-3 border-b border-gray-100 flex flex-wrap items-center gap-3">
          <div role="group" aria-label="Outcome" className="flex gap-2">
            {OUTCOMES.map(o => (
              <button
                key={o.key}
                onClick={() => setOutcome(o.key)}
                aria-pressed={outcome === o.key}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${outcome === o.key
                  ? 'bg-stanford-red text-white border-stanford-red'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'}`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <SearchBox value={query} onChange={setQuery} label="Search decisions" placeholder="Claimant, auditor, reason..." />
          <span className="text-sm text-gray-500" role="status">
            {decisions.length} of {(data?.decisionsList || []).length}
          </span>
          <label className="flex items-center gap-2 text-sm text-gray-600 sm:ml-auto">
            Show
            <select
              aria-label="Decisions per page"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="min-h-[44px] sm:min-h-0 px-2 py-1.5 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-stanford-red focus:border-transparent"
            >
              {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            per page
          </label>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stanford-red text-white">
              <tr>
                <th className={TH}>Date</th>
                <th className={TH}>Outcome</th>
                <th className={TH}>How</th>
                <th className={TH}>Claim type</th>
                <th className={TH}>Claimant</th>
                <th className={TH}>Region</th>
                <th className={TH}>Auditor</th>
                <th className={TH}>Reason / note</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((d, i) => (
                <tr key={`${d.claimId}-${d.performedAt}-${pageStart + i}`} className="border-b border-gray-100 hover:bg-gray-50 align-top">
                  <td className={`${TD} whitespace-nowrap`}>{dateTime(d.performedAt)}</td>
                  <td className={TD}>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${d.outcome === 'returned'
                      ? 'bg-amber-100 text-amber-800'
                      : 'bg-green-100 text-green-800'}`}
                    >
                      {outcomeLabel(d)}
                    </span>
                  </td>
                  <td className={TD}>{d.via || '-'}</td>
                  <td className={TD}>{claimTypeLabel(d.claimType)}</td>
                  <td className={`${TD} font-medium text-gray-800`}>{d.userName}</td>
                  <td className={TD}>{d.region || '-'}</td>
                  <td className={TD}>{d.auditor}</td>
                  <td className={TD}>{d.comments || <span className="text-gray-400">-</span>}</td>
                </tr>
              ))}
              {decisions.length === 0 && (
                <EmptyRow
                  colSpan={8}
                  text={(data?.decisionsList || []).length === 0
                    ? `No audit decisions in ${periodText}.`
                    : 'No decision matches these filters.'}
                />
              )}
            </tbody>
          </table>
        </div>
        {decisions.length > pageSize && (
          <nav aria-label="Decision pages" className="px-4 sm:px-6 py-3 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
            <span role="status">
              Showing {pageStart + 1}-{Math.min(pageStart + pageSize, decisions.length)} of {decisions.length}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage(currentPage - 1)}
                disabled={currentPage === 0}
                className="min-h-[44px] sm:min-h-0 px-3 py-1.5 border border-gray-300 rounded-lg bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span>Page {currentPage + 1} of {pageCount}</span>
              <button
                type="button"
                onClick={() => setPage(currentPage + 1)}
                disabled={currentPage >= pageCount - 1}
                className="min-h-[44px] sm:min-h-0 px-3 py-1.5 border border-gray-300 rounded-lg bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </nav>
        )}
      </Section>

      <Section title="Report rejections" subtitle="Which claims were the problem, so a rejection reads as one claim's issue rather than the whole report's">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stanford-red text-white">
              <tr>
                <th className={TH}>Rejected</th>
                <th className={TH}>Regions</th>
                <th className={THR}>Claims in report</th>
                <th className={TH}>Claims flagged</th>
                <th className={TH}>Reason</th>
                <th className={TH}><span className="sr-only">Open</span></th>
              </tr>
            </thead>
            <tbody>
              {rejections.map((r, i) => {
                const flagged = r.flaggedClaims || [];
                return (
                  <tr key={r.reportId ? `${r.reportId}-${r.rejectedAt}` : i} className="border-b border-gray-100 hover:bg-gray-50 align-top">
                    <td className={`${TD} whitespace-nowrap`}>{shortDate(r.rejectedAt)}</td>
                    <td className={TD}>{(r.regions || []).join(', ') || r.region || '-'}</td>
                    <td className={TDR}>{r.claimsAffected}</td>
                    <td className={TD}>
                      {flagged.length === 0
                        ? <span className="text-gray-500">None flagged individually</span>
                        : (
                          <ul className="space-y-0.5">
                            {flagged.map((f, j) => (
                              <li key={`${f.claimId}-${j}`}>
                                <span className="font-medium">{f.claimant}</span>
                                <span className="text-gray-500"> #{shortId(f.claimId)}</span>
                                {f.reason ? `: ${f.reason}` : ''}
                              </li>
                            ))}
                          </ul>
                        )}
                    </td>
                    <td className={TD}>{r.comments || <span className="text-gray-400">-</span>}</td>
                    <td className="px-4 py-3 text-right">
                      {r.reportId && (
                        <button
                          onClick={() => openReport(r.reportId)}
                          className="px-3 py-1.5 text-sm text-stanford-red border border-stanford-red rounded-lg hover:bg-stanford-red hover:text-white transition-colors"
                        >
                          Open
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {rejections.length === 0 && <EmptyRow colSpan={6} text={`No report was rejected in ${periodText}.`} />}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Director flags" subtitle="Claims the directors flagged. Kept out of the auditor's return rate, which is the auditor's alone.">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stanford-red text-white">
              <tr>
                <th className={TH}>Date</th>
                <th className={TH}>Flagged by</th>
                <th className={TH}>Claim type</th>
                <th className={TH}>Claimant</th>
                <th className={TH}>Region</th>
                <th className={TH}>Reason</th>
              </tr>
            </thead>
            <tbody>
              {flags.map((f, i) => (
                <tr key={`${f.claimId}-${i}`} className="border-b border-gray-100 hover:bg-gray-50 align-top">
                  <td className={`${TD} whitespace-nowrap`}>{shortDate(f.performedAt)}</td>
                  <td className={TD}>
                    {f.flaggedBy}
                    {f.role && <div className="text-xs text-gray-500">{ROLE_LABEL[f.role] || f.role.replace(/_/g, ' ')}</div>}
                  </td>
                  <td className={TD}>{claimTypeLabel(f.claimType)}</td>
                  <td className={`${TD} font-medium text-gray-800`}>{f.userName}</td>
                  <td className={TD}>{f.region || '-'}</td>
                  <td className={TD}>{f.reason || <span className="text-gray-400">-</span>}</td>
                </tr>
              ))}
              {flags.length === 0 && <EmptyRow colSpan={6} text={`No director flags in ${periodText}.`} />}
            </tbody>
          </table>
        </div>
      </Section>
    </>
  );
};

export default RejectionsTab;
